import { useState } from "react";
import { useFetch, apiPost, apiPut, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader, Panel, Stat } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { Radar, ShieldOff, Ban, Check } from "lucide-react";
import { toast } from "sonner";

const POLICIES = ["OPEN", "CONTROLLED", "RESTRICTED", "PRIVATE"];

const THREATS = [
  ["LLM Prompt Injection & Jailbreaking", "Prompt Security", "HIGH", ["Detect", "Policy Check", "Block / Challenge", "Audit"]],
  ["Autonomous Crawler Swarms", "Crawler Activity", "HIGH", ["Rate Limit", "Identity Verify", "Throttle", "Block"]],
  ["Agentic Privilege Escalation", "Authorization Abuse", "CRITICAL", ["Authorization", "Policy", "Block", "Incident"]],
  ["Shadow AI / Unauthorized Activity", "Autonomous Activity", "HIGH", ["Identify", "Verify", "Deny / Challenge"]],
  ["Autonomous API Abuse", "API Abuse", "HIGH", ["Auth", "Rate Limit", "Scope Enforce", "Block"]],
  ["Autonomous Authorization Abuse", "Authorization Abuse", "CRITICAL", ["Policy", "Scope", "Deny", "Block"]],
  ["Autonomous Identity Impersonation", "Identity", "CRITICAL", ["Credential Check", "Revocation", "Deny"]],
  ["Autonomous Data Access Abuse", "Data Access", "CRITICAL", ["Scope", "Policy", "Human Approval"]],
  ["Autonomous Session Abuse", "Session Activity", "HIGH", ["Session Validate", "Revoke", "Rate Limit"]],
  ["Autonomous Policy Violation", "Policy Violations", "HIGH", ["Policy", "Block", "Audit"]],
  ["Autonomous Resource Abuse", "Resource Abuse", "MEDIUM", ["Quotas", "Rate Limit", "Monitor"]],
  ["Autonomous Multi-Stage Activity", "Autonomous Activity", "CRITICAL", ["Correlate", "Risk", "Contain"]],
  ["Autonomous Revocation Evasion", "Authorization Abuse", "CRITICAL", ["Deny", "Block", "Incident", "Audit"]],
  ["AI-Assisted Human Activity", "Autonomous Activity", "MEDIUM", ["Evidence Classify", "Monitor"]],
];

export default function AIAccess() {
  const ov = useFetch("/ai-access/overview");
  const sources = useFetch("/ai-sources");
  const [tab, setTab] = useState("control");
  if (ov.loading || sources.loading || !ov.data) return <Loader label="Loading access control" />;
  const c = ov.data.counts;

  const op = async (s, operation) => {
    try { await apiPost(`/ai-sources/${s.id}/${operation}`, {}); toast.success(`${s.source_id} ${operation}d`); sources.reload(); ov.reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const setPolicy = async (assetId, policy) => {
    try { await apiPut(`/assets/${assetId}/ai-policy`, { policy, mode: policy === "OPEN" ? "Allow" : policy === "PRIVATE" ? "Deny" : "Authorization Required" }); toast.success("AI access policy updated"); ov.reload(); }
    catch { toast.error("Update failed"); }
  };

  const srcCols = [
    { key: "source_id", label: "Source", render: (r) => <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.source_id} · {r.source_type}</p></div> },
    { key: "target_asset", label: "Target", render: (r) => <span className="font-mono text-[11px] text-slate-400">{r.target_asset || "—"}</span> },
    { key: "scope", label: "Scope / Actions", render: (r) => <span className="font-mono text-[11px] text-slate-400">{r.allowed_actions} {r.scope}</span> },
    { key: "expiration", label: "Expires", render: (r) => <span className="text-xs text-slate-500">{r.expiration || "—"}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "ops", label: "", render: (r) => (
      <div className="flex gap-1.5">
        {r.status === "PENDING" && <button onClick={() => op(r, "approve")} data-testid={`src-approve-${r.id}`} className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-300">Approve</button>}
        {!["BLOCKED","REVOKED"].includes(r.status) && <button onClick={() => op(r, "revoke")} data-testid={`src-revoke-${r.id}`} className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300">Revoke</button>}
        {r.status !== "BLOCKED" && <button onClick={() => op(r, "block")} data-testid={`src-block-${r.id}`} className="rounded border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] text-red-300">Block</button>}
      </div>
    ) },
  ];

  const decTone = { ALLOWED: "text-emerald-400", BLOCKED: "text-red-400", DENIED: "text-red-400", CHALLENGED: "text-amber-300", RATE_LIMITED: "text-amber-300" };

  return (
    <div>
      <PageHeader icon={Radar} title="AI Access Control"
        subtitle="AI can request access. AI cannot assume access. Control autonomous activity against verified, connected assets — capability is not authority."
        actions={<DemoTag />} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Verified Sources" value={c.verified_sources} tone="emerald" />
        <Stat label="Authorized" value={c.authorized} tone="blue" />
        <Stat label="Restricted" value={c.restricted} tone="amber" />
        <Stat label="Blocked" value={c.blocked} tone="red" />
        <Stat label="Pending" value={c.pending} tone="amber" />
        <Stat label="Active Policies" value={c.active_policies} tone="cyan" />
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-800">
        {[["control", "Access Control"], ["events", "Live Events"], ["catalog", "Threat Vector Catalog"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`aiaccess-tab-${k}`}
            className={`border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-blue-500 text-blue-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "control" && (
          <div className="space-y-4">
            <Panel title="Autonomous Sources" desc="Authorized, restricted, pending and blocked sources">
              <Table columns={srcCols} rows={sources.data} testid="ai-sources-table" empty="No sources" />
            </Panel>
            <Panel title="Per-Asset AI Access Policy" desc="Each asset controls its own autonomous-access policy — never one blanket rule">
              <div className="space-y-2">
                {ov.data.asset_policies.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5">
                    <div className="min-w-0"><p className="truncate text-sm text-slate-200">{a.name}</p><p className="font-mono text-[11px] text-slate-500">{a.type} · {a.ai_access.mode}</p></div>
                    <select value={a.ai_access.policy} onChange={(e) => setPolicy(a.id, e.target.value)} data-testid={`ai-policy-${a.id}`}
                      className="h-9 rounded-md border border-slate-800 bg-slate-900/70 px-2 text-xs text-slate-200">
                      {POLICIES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        )}

        {tab === "events" && (
          <Panel title="AI / Automated Access — Live Events" desc="DEMO / SIMULATED">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead><tr className="border-b border-slate-800 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {["Time", "Source", "Target", "Action", "Authorization", "Risk", "Decision"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
                </tr></thead>
                <tbody>
                  {ov.data.events.map((e) => (
                    <tr key={e.id} className="border-b border-slate-800/60">
                      <td className="px-3 py-2 text-xs text-slate-500">{timeAgo(e.ts)}</td>
                      <td className="px-3 py-2"><span className="font-mono text-[11px] text-slate-300">{e.source_type}</span><br /><span className="font-mono text-[10px] text-slate-600">{e.source_id}</span></td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-400">{e.target_asset}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-400">{e.action}</td>
                      <td className="px-3 py-2"><span className={`font-mono text-[11px] ${e.authorization === "VALID" ? "text-emerald-400" : "text-amber-400"}`}>{e.authorization}</span></td>
                      <td className="px-3 py-2"><RiskBadge value={e.risk} /></td>
                      <td className={`px-3 py-2 font-mono text-[11px] font-semibold ${decTone[e.decision] || "text-slate-300"}`}>{e.decision}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {tab === "catalog" && (
          <div>
            <p className="mb-4 max-w-2xl text-sm text-slate-400">Understand how autonomous and AI-assisted activity can challenge authorization boundaries — and how ShieldX detects and controls it. Defensive catalog only.</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {THREATS.map(([name, cat, risk, controls]) => (
                <div key={name} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{cat}</span>
                    <RiskBadge value={risk} />
                  </div>
                  <h3 className="mt-2 font-display text-base font-semibold text-slate-100">{name}</h3>
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1 text-emerald-400"><Check className="h-3 w-3" /> Detection Active</span>
                    <span>· Simulation Available</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {controls.map((ct) => <span key={ct} className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 font-mono text-[10px] text-slate-300">{ct}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
